import React from 'react';
import { Archive, Trash2, Star, FileText, Image as ImageIcon, FileArchive, File } from 'lucide-react';
import { motion } from 'framer-motion';

const getFileIcon = (contentType, fileName) => {
    const type = contentType?.toLowerCase() || '';
    const name = fileName?.toLowerCase() || '';

    if (type.includes('image')) return <ImageIcon size={14} style={{ color: '#4285f4' }} />;
    if (type.includes('pdf')) return <FileText size={14} style={{ color: '#ea4335' }} />;
    if (name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z')) return <FileArchive size={14} style={{ color: '#fbbc04' }} />;
    return <File size={14} style={{ color: '#5f6368' }} />;
};

const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const MailItem = ({
    id,
    sender,
    subject,
    snippet,
    time,
    isRead,
    isSelected,
    onToggleSelect,
    onToggleRead,
    isImportant,
    onToggleStar,
    onClick,
    onAction,
    onContextMenu,
    attachments = []
}) => {
    const displayIdentifier = React.useMemo(() => {
        const senderFull = sender || "";
        if (senderFull.includes('<')) {
            return senderFull.split('<')[0].trim();
        }
        const username = senderFull.includes('@') ? senderFull.split('@')[0] : senderFull;
        return username.charAt(0).toUpperCase() + username.slice(1);
    }, [sender]);

    const formattedTime = React.useMemo(() => {
        if (!time) return "";
        
        // Ensure the date is treated as UTC if it lacks timezone info (e.g. from MySQL)
        let normalizedTime = time;
        if (typeof time === 'string' && !time.includes('Z') && !time.includes('+')) {
            normalizedTime = time.includes('T') ? time + 'Z' : time.replace(' ', 'T') + 'Z';
        }
        
        const date = new Date(normalizedTime);
        const now = new Date();
        
        const isToday = date.toDateString() === now.toDateString();

        if (isToday) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        }
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }, [time]);

    const totalAttachmentsSize = React.useMemo(() => {
        return attachments.reduce((sum, att) => sum + (att.fileSize || 0), 0);
    }, [attachments]);

    return (
        <motion.div
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0 16px',
                height: '40px', // Gmail row height is very compact
                cursor: 'pointer',
                position: 'relative',
                background: isRead ? 'rgba(242, 245, 245, 0.8)' : '#fff',
                borderBottom: '1px solid #f1f3f4',
                transition: 'all 0.05s ease',
                zIndex: 1,
                fontSize: '0.875rem',
                color: '#202124'
            }}
            onClick={onClick}
            className="mail-item-row"
            onContextMenu={(e) => {
                if (onContextMenu) {
                    e.preventDefault();
                    onContextMenu(e);
                }
            }}
        >
            {/* Left Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '16px', flexShrink: 0 }}>
                <div
                    onClick={(e) => { e.stopPropagation(); onToggleSelect(id); }}
                    style={{ display: 'flex', alignItems: 'center', padding: '6px', opacity: isSelected ? 1 : 0.2, transition: 'opacity 0.2s' }}
                    className="checkbox-wrapper"
                >
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => { }}
                        style={{
                            cursor: 'pointer',
                            width: '15px',
                            height: '15px',
                        }}
                    />
                </div>
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onToggleStar) onToggleStar(id, !isImportant);
                    }}
                    style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px',
                        color: isImportant ? '#f59e0b' : '#dae0e5',
                    }}
                    className="star-wrapper"
                >
                    <Star
                        size={18}
                        fill={isImportant ? '#f59e0b' : 'transparent'}
                        strokeWidth={isImportant ? 0 : 1.5}
                    />
                </div>
            </div>

            {/* Sender */}
            <div style={{
                width: '180px',
                fontWeight: isRead ? '400' : '700',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginRight: '16px',
                flexShrink: 0,
                fontFamily: "'Wix Madefor Display', sans-serif"
            }}>
                {displayIdentifier}
            </div>

            {/* Subject & Snippet Area */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', overflow: 'hidden', flex: 1 }}>
                    <span style={{
                        fontWeight: isRead ? '400' : '700',
                        whiteSpace: 'nowrap',
                        fontFamily: "'Wix Madefor Display', sans-serif"
                    }}>
                        {subject}
                    </span>
                    <span style={{
                        color: '#5f6368',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '0.85rem'
                    }}>
                        - {snippet}
                    </span>
                </div>

                {/* Attachments Icons (Subtle) */}
                {attachments.length > 0 && (
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginRight: '8px' }}>
                        {attachments.slice(0, 3).map((att, i) => (
                            <span key={i} title={att.fileName}>{getFileIcon(att.contentType, att.fileName)}</span>
                        ))}
                        {attachments.length > 3 && <span style={{ fontSize: '0.7rem', color: '#5f6368' }}>+{attachments.length - 3}</span>}
                    </div>
                )}
            </div>

            {/* Right Side: Size & Time */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, marginLeft: '16px' }}>
                {totalAttachmentsSize > 0 && (
                    <span style={{
                        fontSize: '0.7rem',
                        color: '#5f6368',
                        opacity: 0.6,
                        fontFamily: "'Inter', sans-serif"
                    }}>
                        {formatSize(totalAttachmentsSize)}
                    </span>
                )}
                <div style={{
                    fontSize: '0.75rem',
                    fontWeight: isRead ? '400' : '700',
                    color: isRead ? '#5f6368' : '#202124',
                    whiteSpace: 'nowrap',
                    width: '56px',
                    textAlign: 'right',
                    fontFamily: "'Wix Madefor Display', sans-serif"
                }}>
                    {formattedTime}
                </div>
            </div>

            {/* Hover Actions Bar */}
            <div
                className="hover-actions-gmail"
                style={{
                    display: 'none',
                    alignItems: 'center',
                    gap: '12px',
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#fff',
                    padding: '0 8px',
                    height: '100%',
                    zIndex: 2
                }}
            >
                <div
                    title="Archive"
                    onClick={(e) => { e.stopPropagation(); onAction('Archive', id); }}
                    style={{ cursor: 'pointer', color: '#5f6368', padding: '8px' }}
                >
                    <Archive size={18} />
                </div>
                <div
                    title="Delete"
                    onClick={(e) => { e.stopPropagation(); onAction('Trash', id); }}
                    style={{ cursor: 'pointer', color: '#5f6368', padding: '8px' }}
                >
                    <Trash2 size={18} />
                </div>
            </div>

            <style>{`
                .mail-item-row:hover {
                    box-shadow: inset 1px 0 0 #dadce0, inset -1px 0 0 #dadce0, 0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15);
                    z-index: 2;
                }
                .mail-item-row:hover .hover-actions-gmail {
                    display: flex;
                }
                .mail-item-row:hover .checkbox-wrapper {
                    opacity: 1 !important;
                }
            `}</style>
        </motion.div>
    );
};

export default MailItem;
